import React, { useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Bell, ChevronDown, LogOut, Search, UserRound } from "lucide-react";
import AppearanceToggle from "./AppearanceToggle";
import { applicantNavigationItems } from "../config/applicantNavigation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { clearStoredAuth } from "../utils/authSession";

function getIdentity() {
  const userid = localStorage.getItem("userid") || "Applicant";
  const firstname = localStorage.getItem("firstname") || "";
  const lastname = localStorage.getItem("lastname") || "";
  const displayName = [firstname, lastname].filter(Boolean).join(" ").trim() || userid;
  return { userid, displayName };
}

export default function ApplicantShell({
  title,
  subtitle,
  actions = null,
  notifications = [],
  searchItems = [],
  children,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const identity = useMemo(() => getIdentity(), []);

  const activeItem = applicantNavigationItems.find((item) => item.match?.(location));
  const searchCatalog = useMemo(() => {
    const byKey = new Map();
    [...applicantNavigationItems, ...(searchItems || [])].forEach((item) => {
      if (!item?.href || !item?.label) return;
      const key = `${item.href}:${item.label}`;
      if (!byKey.has(key)) byKey.set(key, item);
    });
    return Array.from(byKey.values());
  }, [searchItems]);

  const filteredSearchItems = searchCatalog.filter((item) => {
    const haystack = `${item.label} ${item.description || ""}`.toLowerCase();
    return haystack.includes(searchQuery.trim().toLowerCase());
  });

  const visibleNotifications = notifications.length
    ? notifications
    : [{
        key: "dashboard",
        title: activeItem?.label || "Dashboard",
        text: "Die Applicant-Navigation bleibt oben, Details liegen in lokalen Panel-Ansichten.",
        href: location.pathname + location.search,
      }];

  const goTo = (href) => {
    setSearchOpen(false);
    setSearchQuery("");
    navigate(href);
  };

  const handleLogout = () => {
    clearStoredAuth();
    window.location.assign("/login");
  };

  return (
    <div className="applicant-shell">
      <header className="applicant-topnav">
        <div className="applicant-topnav__brand">
          <div className="applicant-brand-mark">E4E</div>
          <div className="applicant-brand-copy">
            <span>Experts4Europe</span>
            <strong>Applicant Mission Control</strong>
          </div>
        </div>

        <nav className="applicant-topnav__links" aria-label="Applicant Navigation">
          {applicantNavigationItems.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              className={`applicant-topnav__link${item.match?.(location) ? " is-active" : ""}`}
            >
              <item.icon size={16} strokeWidth={2} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="applicant-topnav__tools">
          <button
            type="button"
            className="applicant-utility-button"
            aria-label="Suche oeffnen"
            onClick={() => setSearchOpen(true)}
          >
            <Search size={16} strokeWidth={2} />
            <span>Suche</span>
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="applicant-utility-button" aria-label="Benachrichtigungen">
                <Bell size={16} strokeWidth={2} />
                <span>Hinweise</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="applicant-menu">
              <DropdownMenuLabel>Benachrichtigungen</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {visibleNotifications.map((item) => (
                <DropdownMenuItem key={item.key || item.href} onSelect={() => goTo(item.href)}>
                  <div className="applicant-menu__item">
                    <strong>{item.title}</strong>
                    <span>{item.text}</span>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <AppearanceToggle compact />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="applicant-profile-button" aria-label="Profilmenue">
                <div className="applicant-profile-button__avatar">
                  <UserRound size={16} strokeWidth={2} />
                </div>
                <div className="applicant-profile-button__copy">
                  <strong>{identity.displayName}</strong>
                  <span>{identity.userid}</span>
                </div>
                <ChevronDown size={14} strokeWidth={2} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="applicant-menu">
              <DropdownMenuLabel>Profilmenue</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => goTo("/dashboard/applicant")}>Dashboard</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => goTo("/dashboard/applicant/edit-profile")}>Profil</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => goTo("/dashboard/account")}>Konto</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={handleLogout}>
                <span className="applicant-menu__logout">
                  <LogOut size={15} strokeWidth={2} />
                  <span>Abmelden</span>
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="applicant-shell__content">
        {(title || subtitle || actions) ? (
          <section className="applicant-page-head">
            <div className="applicant-page-head__copy">
              <span className="applicant-page-head__eyebrow">{activeItem?.label || "Applicant"}</span>
              {title ? <h1>{title}</h1> : null}
              {subtitle ? <p>{subtitle}</p> : null}
            </div>
            {actions ? <div className="applicant-page-head__actions">{actions}</div> : null}
          </section>
        ) : null}

        {children}
      </main>

      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="applicant-search-dialog">
          <DialogHeader>
            <DialogTitle>Schnellzugriff</DialogTitle>
            <DialogDescription>
              Suche nach Applicant-Bereichen, Aktionen und priorisierten Chancen.
            </DialogDescription>
          </DialogHeader>
          <input
            type="search"
            className="applicant-search-dialog__input"
            placeholder="Dashboard, Profil, B1, Matches ..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            autoFocus
          />
          <div className="applicant-search-dialog__results">
            {filteredSearchItems.length ? filteredSearchItems.map((item) => (
              <button
                key={`${item.href}-${item.label}`}
                type="button"
                className="applicant-search-dialog__item"
                onClick={() => goTo(item.href)}
              >
                <strong>{item.label}</strong>
                <span>{item.description || item.href}</span>
              </button>
            )) : (
              <div className="applicant-search-dialog__empty">
                Keine passende Applicant-Aktion gefunden.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
